import { clearAppError, type AppErrorDetails, showAppError } from '../ui/errors.js';
import type { AppState } from '../state/appState.js';
import type { ViewportResolution } from './viewportResolution.js';
import { syncViewportRenderModeControls } from '../ui/viewportRenderModeControls.js';

export interface RenderStatusPresenter {
  updateRenderMode(appState: AppState, renderSlot: 'geometry' | 'material'): void;
  showEmptyViewport(status: string): void;
  showRenderSuccess(params: {
    backend: AppState['backend'];
    sourceLabel: string;
    sizeMode: AppState['viewportSizeMode'];
    resolution: ViewportResolution;
    elapsedMs: number;
    warning?: string;
  }): void;
  showRenderError(params: {
    status: string;
    error: AppErrorDetails;
  }): void;
}

function setText(id: string, value: string): void {
  const el = document.getElementById(id);
  if (el) el.textContent = value;
}

export function createRenderStatusPresenter(): RenderStatusPresenter {
  return {
    updateRenderMode(appState, renderSlot): void {
      syncViewportRenderModeControls(appState, renderSlot);
    },

    showEmptyViewport(status): void {
      clearAppError();
      setText('status-left', status);
      setText('status-right', '');
    },

    showRenderSuccess({ backend, sourceLabel, sizeMode, resolution, elapsedMs, warning }): void {
      setText('render-time', `${elapsedMs.toFixed(1)}ms`);
      const backendLabel = backend === 'webgpu' ? 'WebGPU' : 'Canvas';
      const sizeLabel = sizeMode === 'fit' ? 'Auto' : 'Fixed';
      const statusBase = `${backendLabel} • ${sourceLabel} • ${sizeLabel} • ${resolution.w}×${resolution.h}`;
      setText('status-left', warning ? `${statusBase} • ${warning}` : statusBase);
      clearAppError();
      setText('status-right', '');
    },

    showRenderError({ status, error }): void {
      setText('status-left', status);
      showAppError(error);
      setText('status-right', '');
    },
  };
}
